"use client";
import { type ReactNode, useEffect, useRef } from "react";
import { BOAT_SECTION_ID, getDisplayedBoat } from "../_utils/boat";
import type { Departure } from "../getDeparture";
import type { Travel } from "../getTravel";
import type { Version } from "../getVersion";
import { GeoJSONMap } from "./GeoJSONMap";
import { MainMap } from "./MainMap";
import { Menu } from "./Menu";

type Props = {
  travel: Travel;
  departure: Departure | null;
  version: Version | null;
  head: ReactNode;
  description: ReactNode;
  program: ReactNode;
  speakers: ReactNode;
  hotels: ReactNode;
  transportAir: ReactNode;
  transportTrain: ReactNode;
  transportRoad: ReactNode;
  info: ReactNode;
  more: ReactNode;
  departures: ReactNode;
};
export const ContentClient = ({
  travel,
  departure,
  version,
  head,
  description,
  program,
  speakers,
  hotels,
  transportAir,
  transportTrain,
  transportRoad,
  info,
  more,
  departures,
}: Props) => {
  const presentationRef = useRef<HTMLDivElement | null>(null);
  const programmeRef = useRef<HTMLDivElement | null>(null);
  const speakersRef = useRef<HTMLDivElement | null>(null);
  const hotelsRef = useRef<HTMLDivElement | null>(null);
  const boatRef = useRef<HTMLDivElement | null>(null);
  const transportAirRef = useRef<HTMLDivElement | null>(null);
  const moreRef = useRef<HTMLDivElement | null>(null);
  const departuresRef = useRef<HTMLDivElement | null>(null);

  const hasBoatTab = travel.travelTypeId === "croisiere" && getDisplayedBoat(travel, departure) !== null;

  useEffect(() => {
    boatRef.current = document.getElementById(BOAT_SECTION_ID) as HTMLDivElement | null;
  });

  const booking = departure?.bookings[0];
  const airBookings = departure?.bookings.filter(
    (booking) => booking.transportationMean === "air" && booking.routingAirs.length !== 0,
  );

  return (
    <>
      <div className="relative h-full" ref={presentationRef}>
        {head}
        <Menu
          travel={travel}
          departure={departure}
          items={[
            {
              title: "Présentation",
              element: presentationRef,
              scrollOffset: 16,
            },
            ...(hasBoatTab
              ? [
                  {
                    title: "Bateau",
                    element: boatRef,
                    scrollOffset: 16,
                  },
                ]
              : []),
            {
              title: "Programme",
              element: programmeRef,
              scrollOffset: 16,
            },
            {
              title: "Conférencier",
              element: speakersRef,
              scrollOffset: 16,
            },
            ...(!travel.isTailorMade
              ? [
                  {
                    title: "Dates et tarifs",
                    element: departuresRef,
                    scrollOffset: 16,
                  },
                ]
              : []),
            ...(!hasBoatTab
              ? [
                  {
                    title: "Hébergement",
                    element: hotelsRef,
                    scrollOffset: 16,
                  },
                ]
              : []),
            {
              title: "Transport",
              element: transportAirRef,
              scrollOffset: 16,
            },
            {
              title: "À savoir",
              element: moreRef,
              scrollOffset: 16,
            },
          ]}
        />
        {description}
        {departure && version && (version.status !== 0 || travel.isTailorMade) && (
          <>
            {version.mapData && !(version.mapGeoJSON && version.mapStatus) && <MainMap data={version.mapData} />}
            {version.mapGeoJSON && version.mapStatus && <GeoJSONMap geoJSON={version.mapGeoJSON} />}
            <div ref={programmeRef}>{program}</div>
          </>
        )}
        {departure && departure.speakers.length !== 0 && <div ref={speakersRef}>{speakers}</div>}
        {departures && <div ref={departuresRef}>{departures}</div>}
        {departure &&
          version &&
          (departure.hotels.length !== 0 || version.hotels.length !== 0 || version.hostingComment) &&
          departure.state.registrationStatus !== "soon" && <div ref={hotelsRef}>{hotels}</div>}
        {departure && departure.state.registrationStatus !== "soon" && airBookings && airBookings.length !== 0 && (
          <>
            <h2
              className="text-primary text-[22px] leading-7 lg:text-[26px] lg:leading-8 font-bold mt-8 lg:mt-14"
              ref={transportAirRef}
            >
              Vols au départ de Paris
            </h2>
            {transportAir}
          </>
        )}
        {departure &&
          departure.state.registrationStatus !== "soon" &&
          booking &&
          booking.transportationMean === "train" &&
          booking.routingTrains.length !== 0 &&
          transportTrain}
        {departure &&
          departure.state.registrationStatus !== "soon" &&
          booking &&
          booking.transportationMean === "road" &&
          booking.routingRoads.length !== 0 &&
          transportRoad}
        {travel.travelTypeId !== "sejour-un-jour" && info}
        {departure && departure?.state.versionStatus !== "soon" && (
          <>
            <p
              className="text-primary text-[22px] leading-7 lg:text-[26px] lg:leading-8 font-bold mt-8 lg:mt-14"
              ref={moreRef}
            >
              À savoir
            </p>
            {more}
          </>
        )}
      </div>
    </>
  );
};
